'use client'

import { motion } from 'framer-motion'
import { Target, Eye, Award, Users, Lightbulb, Shield } from 'lucide-react'

const values = [
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'We believe privacy is a fundamental right.'
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Constantly pushing the boundaries of what\'s possible in educational technology and privacy.'
  },
  {
    icon: Users,
    title: 'User-Centric',
    description: 'Every decision we make is guided by what\'s best for our users, especially children and families.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We strive for high-quality design and user experience across all our products.'
  }
]

export default function About() {
  return (
    <section id="about" className="py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Company Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            About <span className="gradient-text">Elyndra Studios</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Elyndra Studios Ltd is an early-stage technology company currently developing innovative 
            solutions that bridge the gap between education and digital innovation. We are building 
            platforms that will empower young minds while respecting their privacy.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Mission & Vision */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                To develop and launch innovative, educational technology solutions that will empower the next 
                generation of entrepreneurs while respecting their privacy.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                A world where children will be able to explore, learn, and build their entrepreneurial skills 
                in digital environments that respect their privacy and foster their creativity.
              </p>
            </div>
          </motion.div>

          {/* Stats/Achievements */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-6"
          >
            <div className="card text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">Pre-Launch</div>
              <p className="text-gray-600">Development Stage</p>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-secondary-600 mb-2">Mar 2025</div>
              <p className="text-gray-600">Founded</p>
            </div>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-center mb-12">Our Core Values</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card text-center group hover:scale-105 transition-transform duration-200"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h4>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technology Focus */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-3xl p-8 md:p-12"
        >
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-6">Technology Excellence</h3>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Our commitment to cutting-edge technology ensures that our solutions will be innovative, 
              scalable, and future-ready when we launch.
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800">Privacy-first approach</h4>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800">Scalable Infrastructure</h4>
                <p className="text-gray-600 text-sm">Cloud-native solutions being built to grow with your needs.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
